import { queryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import type { DashboardResponse, DashboardTab } from '@/features/dashboard/types';
import { http } from '@/services/http';

import { getDashboardApiUrl, getDashboardShareUrl } from './dashboardUrl';
import { normalizeDashboardEntry } from './normalizeDashboard';

const normalizeDashboardResponse = (
  data: Record<string, DashboardResponse>
): Record<string, DashboardTab> =>
  Object.fromEntries(
    Object.entries(data).map(([name, value]) => [name, normalizeDashboardEntry(name, value)])
  );

const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

const getEmptyDashboardCache = (): Record<string, DashboardTab> => ({});

export const dashboardOptions = {
  all: () => ['dashboard'] as const,
  sharedAll: () => ['shared-dashboard'] as const,

  mutations: {
    all: () => [...dashboardOptions.all(), 'mutation'] as const,
    addWidget: () => [...dashboardOptions.mutations.all(), 'add-widget'] as const,
    create: () => [...dashboardOptions.mutations.all(), 'create'] as const,
    delete: (dashboardName: string) =>
      [...dashboardOptions.mutations.all(), 'delete', dashboardName] as const,
    generateShareLink: (dashboardName: string) =>
      [...dashboardOptions.mutations.all(), 'generate-share-link', dashboardName] as const,
    save: () => [...dashboardOptions.mutations.all(), 'save'] as const,
    sendShareEmail: () => [...dashboardOptions.mutations.all(), 'send-share-email'] as const,
    updateAll: () => [...dashboardOptions.mutations.all(), 'update'] as const,
    update: (dashboardName: string, oldDashboardName?: string) =>
      [...dashboardOptions.mutations.updateAll(), dashboardName, oldDashboardName ?? null] as const
  },

  list: () =>
    queryOptions({
      queryKey: dashboardOptions.all(),
      queryFn: async () => {
        try {
          const { data } =
            await http.internal.get<Record<string, DashboardResponse>>(getDashboardApiUrl());
          return normalizeDashboardResponse(data);
        } catch (error) {
          if (isNotFoundError(error)) {
            return getEmptyDashboardCache();
          }
          throw error;
        }
      },
      retry: (failureCount, error) => {
        if (isNotFoundError(error)) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      staleTime: Infinity,
      structuralSharing: false
    }),

  shared: (shareToken: string | null) =>
    queryOptions({
      queryKey: [...dashboardOptions.sharedAll(), shareToken ?? ''] as const,
      queryFn: async () => {
        const { data } = await http.internal.get<Record<string, DashboardResponse>>(
          getDashboardShareUrl(),
          {
            params: { shareToken }
          }
        );
        return normalizeDashboardResponse(data);
      },
      enabled: !!shareToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      structuralSharing: false
    })
};
